import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
// material
import {
  Card,
  Table,
  Stack,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Box } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
// components
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import BrudCrumbs from '../../components/BreadCrumbs';
import PageTitle from '../../components/PageHeading';
import USERLIST from '../../_mocks_/user';
import JourneyTemplateToolbar from './JourneyTemplateToolbar';
import { StyledTableCell, StyledTableRow } from '../../utils/StyleTable';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useJourneySelector from '../../store/Slice/Journey/JourneySelector';
import { getJourneyTemplate } from '../../store/Slice/Journey/JourneySlice';
import { saveApiData } from '../../store/Slice/Global/GlobalSlice';
import { JourneyApi } from '../../utils/Apis';
import { normalizeValue } from '../../utils/CommonFunction';

// ----------------------------------------------------------------------
const TABLE_HEAD = [
  { id: 'id', label: 'Template ID', alignRight: false },
  { id: 'templateName', label: 'Template Name', alignRight: false },
  { id: 'loanType', label: 'Loan Type', alignRight: false },
  { id: 'loanSubType', label: 'Loan Sub Type', alignRight: false },
  { id: 'employmentType', label: 'Employment Type', alignRight: false },
  { id: '', label: 'ACTION' }
];

export default function JourneyTemplate() {
  const [page, setPage] = useState(0);
  const [page1, setPage1] = useState(1);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(600);
  const [copyConfirm, setCopyConfirm] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateId, setTemplateId] = useState(null);

  const dispatch = useDispatch();

  const { LoginData } = useLoginSelector();
  const { TemplateData } = useJourneySelector();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = USERLIST.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - USERLIST.length) : 0;

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleCopyDialog = (e, rowId, templateName) => {
    setTemplateName(templateName);
    setTemplateId(rowId);
    setCopyConfirm(true);
  };

  const handleCopy = async (e) => {
    const credentials = {
      companyId: LoginData?.companyId,
      templateId: templateId || null,
      userId: LoginData?.userId
    };
    try {
      const res = await dispatch(saveApiData(JourneyApi?.TemplateCopy, credentials));
      if (res?.status === 'success') {
        getJourneyTemplateApi();
        setCopyConfirm(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const getJourneyTemplateApi = useCallback(() => {
    if (!LoginData) return '';
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      entityListRequestModel: {
        pageNumber: page1,
        pageSize: rowsPerPage,
        searchLiteral: filterName,
        sortOrder: order
      },
      journeyTemplateFilterModel: {
        sourceJourneyId: null,
        templateName: ''
      }
    };
    dispatch(getJourneyTemplate(credentials));
  }, [LoginData, page1, rowsPerPage, filterName, order]);

  useEffect(() => {
    getJourneyTemplateApi();
  }, [getJourneyTemplateApi]);

  return (
    <Page title="Partner Managemet System || Journey">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Template" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Card>
            <JourneyTemplateToolbar filterName={filterName} onFilterName={handleFilterByName} />
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800, height: '70vh', overflow: 'auto' }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={TemplateData?.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    isCheckBox="true"
                  />
                  <TableBody>
                    {TemplateData &&
                      TemplateData?.length > 0 &&
                      TemplateData?.map((item, index) => {
                        const isItemSelected = selected.indexOf(item) !== -1;
                        return (
                          <StyledTableRow
                            hover
                            key={index}
                            tabIndex={-1}
                            role="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isItemSelected}
                                onChange={(event) => handleClick(event, item)}
                              />
                            </TableCell>
                            <StyledTableCell align="left">
                              {normalizeValue(item?.templateId, 'N/A')}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {normalizeValue(item?.templateName, 'N/A')}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {normalizeValue(item?.templateBody?.loanType?.parentLoanName, 'N/A')}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {normalizeValue(item?.templateBody?.loanSubType?.loanName, 'N/A')}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {normalizeValue(
                                item?.templateBody?.employmentType?.employmentCategoryName,
                                'N/A'
                              )}
                            </StyledTableCell>
                            <StyledTableCell align="" width={110}>
                              <UserMoreMenu
                                rowId={item?.templateId}
                                templateName={item?.templateName}
                                parent="journey-template"
                                setCopyConfirm={setCopyConfirm}
                                handleCopyDialog={handleCopyDialog}
                              />
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <StyledTableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                  {TemplateData?.length === 0 && (
                    <TableBody>
                      <TableRow>
                        <StyledTableCell
                          align="center"
                          colSpan={TABLE_HEAD.length + 1}
                          sx={{ py: 3 }}
                        >
                          <SearchNotFound searchQuery={filterName} />
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>
          </Card>
        </Box>
      </Container>

      <Dialog
        fullWidth
        className="PopupBox"
        open={copyConfirm}
        onClose={(e) => setCopyConfirm(false)}
      >
        <DialogTitle className="popupTitle">
          <Typography variant="h6">Copy Action Confirmation</Typography>
          <IconButton onClick={(e) => setCopyConfirm(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            Do you want to make a copy of the selected template "{templateName}" ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              setCopyConfirm(false);
            }}
            variant="outlined"
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={(e) => {
              handleCopy(e);
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
